import { css } from '@emotion/react';
import {
  useLayoutEffect, useRef,
} from 'react';
import { AddMorePlaceholderComponent } from '~/_shared/components/addMorePlaceholder/addMorePlaceholder.component';
import type { Uuid } from '~/_shared/utils/createUuid';
import {
  CHART_ITEM_HEIGHT, ChartItemComponent,
} from './chartItem/chartItem.component';
import type { ChartItem } from './chartItem/chartItem.types';

const ADD_NEW_WIDTH = 90;
const GALLERY_GAP = 10;

const addNewStyle = css({
  flexShrink: 0,
  height: CHART_ITEM_HEIGHT,
});

const addNewIconStyle = css({
  fontSize: 40,
});

const containerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: GALLERY_GAP,
});

// Charts are as wide as their content, so a wide first chart would wrap below the add-new button.
// Capping it at the space left next to the button keeps them on the same line.
const WRAP_SAFETY_MARGIN = 2;
const firstChartNextToAddNewStyle = css({
  maxWidth: `calc(100% - ${ADD_NEW_WIDTH + GALLERY_GAP + WRAP_SAFETY_MARGIN}px)`,
  minWidth: 0,
});

export type ChartsGalleryComponentProps = Readonly<{
  className?: string;
  chartItems: ReadonlyArray<ChartItem>;
  scrollToChartItemId?: Uuid;
  onAddNewChart?: () => void;
  onScrolledToChartItem?: () => void;
}>;

export const ChartsGalleryComponent = ({
  className,
  chartItems,
  scrollToChartItemId,
  onAddNewChart,
  onScrolledToChartItem,
}: ChartsGalleryComponentProps) => {
  const onScrolledToChartItemRef = useRef(onScrolledToChartItem);
  const chartContainersRefs = useRef<Record<Uuid, HTMLDivElement | null>>({});

  // Effect to handle scrolling when scrollToChartItemId changes
  useLayoutEffect(() => {
    if (scrollToChartItemId) {
      const chartElement = chartContainersRefs.current[scrollToChartItemId];

      if (chartElement) {
        onScrolledToChartItemRef.current?.();

        setTimeout(() => {
          const chartElementRefAfterTimeout = chartContainersRefs.current[scrollToChartItemId];
          // Scroll the element into view with smooth behavior
          chartElementRefAfterTimeout?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 1000);
      }
    }
  }, [scrollToChartItemId, onScrolledToChartItemRef]);

  return (
    <div className={className} css={containerStyle}>
      {onAddNewChart && (
        <AddMorePlaceholderComponent
          css={addNewStyle}
          width={ADD_NEW_WIDTH}
          onClick={onAddNewChart}
          iconStyle={addNewIconStyle}
        />
      )}
      {chartItems.map((chartItem, index) => (
        <ChartItemComponent
          ref={el => {
            chartContainersRefs.current[chartItem.id] = el;
          }}
          key={chartItem.id}
          css={onAddNewChart && index === 0 ? firstChartNextToAddNewStyle : undefined}
          {...chartItem}
        />
      ))}
    </div>
  );
};
